import styled from "styled-components";
import useRegisterNotice from "features/owner/hooks/useRegisterNotice";
import { FiImage } from "react-icons/fi";
import Modal from "shared/components/Modal";
import LoadingWithProgressBar from "shared/components/Loading";
import RegisterFooter from "shared/components/RegisterFooter";
import { parseDate } from "shared/utils/dateUtils";
import { hw, scrollbar } from "features/homework/homeworkUi";

interface RegisterNoticeProps {
    isBranch: boolean;
    isOpen: boolean;
    onCreateConfirmClickHandler: () => void;
}

const RegisterNotice = ({ isBranch, isOpen, onCreateConfirmClickHandler }: RegisterNoticeProps) => {
    const {
        inputs, onChange,
        inputImageRef, onImageInput, onImageInputClick, showImageListView,
        onSaveNoticeClick, isSucceed, setIsSucceed, progress, showProgres,
        onSucceedModalConfirmClickHandler,
    } = useRegisterNotice({ id: "", writer: "" }, isBranch, false);

    if (!isOpen) return null;

    return (
        <Container>
            <Wrapper>
                <Modal
                    isOpen={isSucceed}
                    title="SUCCESS"
                    content="저장되었습니다"
                    onClose={() => {
                        setIsSucceed(false);
                        onCreateConfirmClickHandler();
                        onSucceedModalConfirmClickHandler();
                    }}
                />
                {showProgres ? (
                    <LoadingWithProgressBar title="이미지 업로드중" subTitle="" progress={progress} />
                ) : null}
                <Header>
                    <div>
                        <Title>공지 작성</Title>
                        <Desc>제목과 내용을 입력한 뒤 저장하세요.</Desc>
                    </div>
                    <Close type="button" onClick={onCreateConfirmClickHandler}>닫기</Close>
                </Header>
                <Content>
                    <TitleCard>
                        <TitleRow>
                            <TitleInput
                                placeholder="제목을 입력해 주세요"
                                name="title"
                                value={inputs.title}
                                onChange={onChange}
                            />
                            <IconButton type="button" onClick={onImageInputClick} aria-label="이미지 추가">
                                <FiImage size={18} />
                            </IconButton>
                            <input
                                type="file"
                                ref={inputImageRef}
                                accept="image/*"
                                onChange={onImageInput}
                                style={{ display: "none" }}
                            />
                        </TitleRow>
                        <Chip>
                            <Label htmlFor="register-expires">만료일</Label>
                            <DateInput
                                id="register-expires"
                                type="date"
                                name="expires_at"
                                value={inputs.expires_at || ""}
                                onChange={onChange}
                                min={parseDate(new Date())}
                            />
                        </Chip>
                    </TitleCard>
                    <NoteCard>
                        <NoteHead>내용</NoteHead>
                        <ContentArea
                            name="content"
                            value={inputs.content}
                            onChange={onChange}
                            placeholder="내용을 입력해 주세요"
                        />
                    </NoteCard>
                    <Images>{showImageListView()}</Images>
                    {!isBranch ? (
                        <Targets>
                            <CheckLabel>
                                <Checkbox type="checkbox" name="student" checked={inputs.student} onChange={onChange} />
                                학생용 공지
                            </CheckLabel>
                            <CheckLabel>
                                <Checkbox type="checkbox" name="teacher" checked={inputs.teacher} onChange={onChange} />
                                교사용 공지
                            </CheckLabel>
                        </Targets>
                    ) : null}
                </Content>
                <RegisterFooter onSaveButtonClick={onSaveNoticeClick} />
            </Wrapper>
        </Container>
    );
};

const Container = styled.div`
    position: absolute;
    inset: 0;
    display: flex;
    padding: 20px 24px;
    z-index: 10;
    background: ${hw.bg};
`;

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 22px 24px 16px;
    border-radius: 16px;
    background: ${hw.surface};
    border: 1px solid ${hw.border};
    box-shadow: ${hw.shadow};
    flex: 1;
    min-height: 0;
    overflow: hidden;
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 12px;
    flex-shrink: 0;
`;

const Title = styled.h1`
    margin: 0;
    color: ${hw.text};
    font-size: 22px;
    font-weight: 700;
    letter-spacing: -0.03em;
`;

const Desc = styled.p`
    margin: 4px 0 0;
    font-size: 13px;
    color: ${hw.textMuted};
`;

const Close = styled.button`
    appearance: none;
    border: 1px solid ${hw.border};
    background: ${hw.surface};
    color: ${hw.textMuted};
    font-size: 13px;
    font-weight: 600;
    border-radius: 8px;
    padding: 8px 12px;
    cursor: pointer;

    &:hover {
        background: ${hw.bg};
        color: ${hw.text};
    }
`;

const Content = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    ${scrollbar}
`;

const TitleCard = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 16px;
    background: ${hw.bg};
    border: 1px solid ${hw.border};
    border-radius: 12px;
`;

const TitleRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const TitleInput = styled.input`
    flex: 1;
    min-width: 0;
    font-size: 18px;
    font-weight: 700;
    border: 1px solid ${hw.border};
    border-radius: 10px;
    outline: none;
    padding: 10px 12px;
    color: ${hw.text};
    background: ${hw.surface};

    &:focus {
        border-color: ${hw.brand};
        box-shadow: 0 0 0 3px rgba(223, 0, 0, 0.12);
    }

    &::placeholder {
        color: ${hw.textSubtle};
        font-weight: 600;
    }
`;

const IconButton = styled.button`
    appearance: none;
    border: 1px solid ${hw.border};
    background: ${hw.surface};
    width: 42px;
    height: 42px;
    border-radius: 10px;
    cursor: pointer;
    color: ${hw.textMuted};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    &:hover {
        background: ${hw.brandSoft};
        border-color: ${hw.brandMuted};
        color: ${hw.brand};
    }
`;

const Chip = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    align-self: flex-start;
    padding: 8px 12px;
    background: ${hw.surface};
    border: 1px solid ${hw.border};
    border-radius: 10px;
`;

const Label = styled.label`
    font-size: 12px;
    font-weight: 600;
    color: ${hw.textMuted};
`;

const DateInput = styled.input`
    border: none;
    outline: none;
    background: transparent;
    color: ${hw.text};
    font-size: 13px;
    font-weight: 600;
`;

const NoteCard = styled.div`
    display: flex;
    flex-direction: column;
    min-height: 240px;
    background: ${hw.bg};
    border: 1px solid ${hw.border};
    border-radius: 12px;
    overflow: hidden;
`;

const NoteHead = styled.div`
    padding: 12px 16px;
    font-size: 13px;
    font-weight: 700;
    color: ${hw.text};
    border-bottom: 1px solid ${hw.border};
    background: ${hw.surface};
`;

const ContentArea = styled.textarea`
    flex: 1;
    min-height: 180px;
    border: none;
    outline: none;
    resize: none;
    font-size: 14px;
    line-height: 1.6;
    padding: 12px 16px;
    color: ${hw.text};
    background: ${hw.bg};
    ${scrollbar}

    &::placeholder {
        color: ${hw.textSubtle};
    }
`;

const Images = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    gap: 10px;

    img {
        width: 100%;
        height: 100px;
        object-fit: cover;
        border-radius: 10px;
        border: 1px solid ${hw.border};
        background: ${hw.surface};
    }
`;

const Targets = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 14px;
    padding: 4px 2px 8px;
`;

const CheckLabel = styled.label`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
    color: ${hw.text};
    font-size: 13px;
    font-weight: 600;
`;

const Checkbox = styled.input`
    width: 16px;
    height: 16px;
    margin: 0;
    cursor: pointer;
    accent-color: ${hw.brand};
`;

export default RegisterNotice;
